import React, { useCallback } from 'react'
import { Box, FancyButton, Paragraph, Text } from '@ui'
import { useOpenMiniappPanel } from 'hooks/useOpenMiniappPanel'
import { MiniappEmbed } from 'use-towns-client'

type MiniappTileProps = {
    embed: MiniappEmbed | null
    miniappUrl: string
    isLoading: boolean
    isAttachmentPreview?: boolean
    eventId?: string
}

export const MiniappTile = ({
    embed,
    miniappUrl,
    isLoading,
    isAttachmentPreview,
    eventId,
}: MiniappTileProps): React.ReactElement | null => {
    const openMiniappPanel = useOpenMiniappPanel()

    const actionType = embed?.button?.action?.type
    const targetUrl = embed?.button?.action?.url || miniappUrl
    const buttonLabel = embed?.button?.title || 'Open miniapp'

    const handleOpen = useCallback(() => {
        if (!targetUrl) {
            console.warn('[MiniappTile] No miniapp URL available to open')
            return
        }

        openMiniappPanel({
            embed,
            miniappUrl: targetUrl,
            eventId,
        })
    }, [embed, eventId, openMiniappPanel, targetUrl])

    if (isLoading) {
        return <MiniappTileSkeleton />
    }

    if (!embed) {
        return null
    }

    // Check if this is a supported miniapp version and action type
    const isLaunchMiniapp =
        actionType === 'launch_miniapp' ||
        actionType === 'launch_frame' ||
        actionType === 'open_url'

    if (!isLaunchMiniapp) {
        // Render debug info for unsupported versions/types
        return (
            <CardContainer isAttachmentPreview={isAttachmentPreview}>
                <Box padding="sm" gap="sm">
                    <Paragraph strong size="md" color="default">
                        Unknown miniapp
                    </Paragraph>
                    <Box
                        as="pre"
                        padding="sm"
                        background="level3"
                        borderRadius="xs"
                        overflow="auto"
                        style={{
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            maxHeight: '300px',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        <Text as="span" size="sm" color="gray1">
                            {JSON.stringify(embed, null, 2)}
                        </Text>
                    </Box>
                </Box>
            </CardContainer>
        )
    }

    // Render proper miniapp card for launch_miniapp version 1
    return (
        <CardContainer isAttachmentPreview={isAttachmentPreview}>
            <Box overflow="hidden">
                <Box
                    as="img"
                    src={embed.imageUrl}
                    alt={embed.button.action.name}
                    width="100%"
                    height="auto"
                    objectFit="cover"
                />
            </Box>
            {isAttachmentPreview && (
                <Box padding="sm" background="level1">
                    <Text size="sm" color="default">
                        {embed.button.action.name}
                    </Text>
                </Box>
            )}
            {!isAttachmentPreview && (
                <Box padding="sm" background="level1">
                    <FancyButton
                        noLayout
                        cta
                        aria-label={`Open miniapp ${embed.button.action.name}`}
                        disabled={!targetUrl}
                        width="100%"
                        borderRadius="sm"
                        onClick={handleOpen}
                    >
                        {buttonLabel}
                    </FancyButton>
                </Box>
            )}
        </CardContainer>
    )
}

const MiniappTileSkeleton = () => (
    <CardContainer isAttachmentPreview={false}>
        <Box background="level3" style={{ width: '100%', height: 'auto' }} />
        <Box padding="sm" gap="sm">
            <SkeletonLine width="60%" />
            <SkeletonLine width="45%" />
            <SkeletonLine width="35%" />
            <SkeletonButton />
        </Box>
    </CardContainer>
)

const SkeletonLine = ({ width }: { width: string }) => (
    <Box background="level3" borderRadius="xs" style={{ height: '10px', width }} />
)

const SkeletonButton = () => (
    <Box background="level3" borderRadius="sm" style={{ height: '40px', width: '100%' }} />
)

const CardContainer = ({
    children,
    isAttachmentPreview,
    ...props
}: React.ComponentProps<typeof Box> & { isAttachmentPreview?: boolean }) => (
    <Box
        background="level2"
        borderRadius={isAttachmentPreview ? 'sm' : 'md'}
        overflow="hidden"
        border="faint"
        style={{ width: isAttachmentPreview ? '100%' : '80%' }}
        gap="none"
        {...props}
    >
        {children}
    </Box>
)
